import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SEOHelmet, {
  siteUrl,
  buildBreadcrumb,
} from "../components/SEOHelmet";
import blogsData from "../data/blogs.json";

const BlogDetails = () => {
  const { slug } = useParams();
  const blog = blogsData.find((b) => b.slug === slug);
  if (!blog) {
    return <Navigate to="/blogs" replace />;
  }
  const publishedDateISO = "2026-06-10T09:00:00+05:30";
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    description: blog.excerpt,
    datePublished: publishedDateISO,
    dateModified: publishedDateISO,
    url: `${siteUrl}/blogs/${blog.slug}`,
    image: `${siteUrl}${blog.image}`,
    author: {
      "@type": "Organization",
      name: "Baby Twinkles Photography",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Baby Twinkles Photography",
      logo: {
        "@type": "ImageObject",
        url: `${siteUrl}/assets/img/logo.png`,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${siteUrl}/blogs/${blog.slug}`,
    },
    keywords: blog.tags.join(", "),
  };

  const categoriesWithCounts = blogsData.reduce((acc, b) => {
    acc[b.category] = (acc[b.category] || 0) + 1;
    return acc;
  }, {});

  const popularTags = Array.from(
    new Set(blogsData.flatMap((b) => b.tags))
  ).slice(0, 10);

  const recentBlogs = blogsData.filter((b) => b.id !== blog.id).slice(0, 6);

  const midImage1 = "/assets/img/babies/hero2.png";
  const midImage2 = "/assets/img/babies/hero3.png";

  return (
    <>
      <SEOHelmet
        title={`${blog.title}`}
        description={blog.excerpt}
        keywords={blog.tags.join(", ")}
        canonicalUrl={`${siteUrl}/blogs/${blog.slug}`}
        ogType="article"
        ogImage={`${siteUrl}${blog.image}`}
        schema={[
          articleSchema,
          buildBreadcrumb([
            { name: "Home", url: `${siteUrl}/` },
            { name: "Blog", url: `${siteUrl}/blogs` },
            { name: blog.title, url: `${siteUrl}/blogs/${blog.slug}` },
          ]),
        ]}
      />
      <div className="inner-page-banner">
        <div className="banner-wrapper">
          <div className="container-fluid">
            <div className="banner-main-content-wrap">
              <div className="row">
                <div className="col-12">
                  <div className="banner-content">
                    <ul className="breadcrumb-list mt-5 pt-5 d-flex justify-content-center align-items-center">
                      <li>
                        <Link to="/">Home</Link>
                      </li>
                      <li>
                        <Link to="/blogs">Blogs</Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="blog-standard-page mt-4">
        <div className="container">
          <div className="row g-lg-4 gy-5">
            <div className="col-lg-8">
              <div className="post-thumb">
                <img
                  className="rounded"
                  src={blog.image}
                  alt={blog.title}
                />
              </div>
              <h3 className="post-title mt-2" style={{ fontSize: "22px" }}>
                {blog.title}
              </h3>
              
              {/* Dynamic content paragraphs */}
              {blog.content.length > 0 && <p>{blog.content[0]}</p>}
              {blog.content.length > 1 && <p>{blog.content[1]}</p>}
              
              {/* Mid-blog images block from the original template */}
              <div className="row g-4 mb-20">
                <div className="col-sm-6">
                  <div className="blog-dt-img">
                    <img
                      className="img-fluid rounded"
                      src={midImage1}
                      alt="Baby Portrait Setup"
                    />
                  </div>
                </div>
                <div className="col-sm-6">
                  <div className="blog-dt-img">
                    <img
                      className="img-fluid rounded"
                      src={midImage2}
                      alt="Newborn Props Setup"
                    />
                  </div>
                </div>
              </div>
              
              {blog.content.slice(2).map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
            <div className="col-lg-4">
              <div className="blog-sidebar mb-4">
                <div className="single-widgets widget_egns_categoris mb-20 d-none d-lg-block">
                  <div className="widget-title mb-20">
                    <h6>Category</h6>
                  </div>
                  <ul className="wp-block-categoris-cloud">
                    {Object.entries(categoriesWithCounts).map(([category, count]) => (
                      <li key={category}>
                        <Link
                          to="/blogs"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            textDecoration: "none"
                          }}
                        >
                          <span>{category}</span>{" "}
                          <span className="number-of-categoris">({count})</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="single-widgets widget_egns_recent_post mb-4 d-none d-lg-block">
                  <div className="widget-title blog-title mb-20">
                    <h6>Recent Blog</h6>
                  </div>
                  <div className="recent-post-wraper">
                    <div className="d-flex flex-column gap-3">
                      {recentBlogs.map((b) => (
                        <div key={b.id} className="widget-cnt d-flex gap-3 align-items-start">
                          <div className="wi flex-shrink-0">
                            <Link to={`/blogs/${b.slug}`} style={{ display: "block", borderRadius: "8px", overflow: "hidden" }}>
                              <img
                                src={b.image}
                                alt={b.title}
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  objectFit: "cover",
                                  borderRadius: "8px"
                                }}
                              />
                            </Link>
                          </div>
                          <div className="wc flex-grow-1">
                            <Link className="date" to="/blogs" style={{ fontSize: "12px", color: "#888", textDecoration: "none" }}>
                              {b.date}
                            </Link>
                            <h6 style={{ fontSize: "14px", margin: "4px 0 0 0", lineHeight: "1.4" }}>
                              <Link to={`/blogs/${b.slug}`} style={{ textDecoration: "none", color: "#333" }}>
                                {b.title}
                              </Link>
                            </h6>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogDetails;
